import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FaceCapture from '../components/FaceCapture';

const styles = {
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '2rem',
    alignItems: 'start',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '16px',
    padding: '2rem',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1.5rem',
    color: '#00d9ff',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  resultCard: {
    textAlign: 'center',
    padding: '2rem',
  },
  matchedCard: {
    background: 'linear-gradient(135deg, rgba(0, 230, 118, 0.1) 0%, rgba(0, 200, 83, 0.1) 100%)',
    border: '2px solid rgba(0, 230, 118, 0.5)',
  },
  unmatchedCard: {
    background: 'linear-gradient(135deg, rgba(255, 82, 82, 0.1) 0%, rgba(255, 23, 68, 0.1) 100%)',
    border: '2px solid rgba(255, 82, 82, 0.5)',
  },
  waitingCard: {
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px dashed rgba(255, 255, 255, 0.2)',
  },
  avatar: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    margin: '0 auto 1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '3rem',
  },
  avatarMatched: {
    background: 'linear-gradient(135deg, #00e676 0%, #00c853 100%)',
  },
  avatarUnmatched: {
    background: 'linear-gradient(135deg, #ff5252 0%, #ff1744 100%)',
  },
  avatarWaiting: {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '2px dashed rgba(255, 255, 255, 0.3)',
  },
  personName: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
  },
  personId: {
    fontSize: '0.9rem',
    color: 'rgba(255, 255, 255, 0.6)',
    marginBottom: '1rem',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
    textAlign: 'left',
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  detailLabel: {
    fontSize: '0.75rem',
    color: 'rgba(255, 255, 255, 0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailValue: {
    fontSize: '0.95rem',
    color: '#fff',
  },
  confidenceBar: {
    marginTop: '1.5rem',
  },
  confidenceLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
    fontSize: '0.85rem',
  },
  confidenceTrack: {
    height: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  confidenceFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.5s ease',
  },
  topMatches: {
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  matchRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0',
    fontSize: '0.9rem',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
    marginBottom: '2rem',
  },
  statCard: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    padding: '1.25rem',
    textAlign: 'center',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#00d9ff',
  },
  statLabel: {
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.6)',
    marginTop: '0.25rem',
  },
  recentLogs: {
    marginTop: '2rem',
  },
  logItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '8px',
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
  },
  instructions: {
    textAlign: 'center',
    padding: '2rem',
    color: 'rgba(255, 255, 255, 0.6)',
  },
};

function RecognizePage() {
  const [result, setResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stats, setStats] = useState({ total_persons: 0, total_faces: 0, entries_today: 0 });
  const [recentLogs, setRecentLogs] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchLogs();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get('/api/logs?limit=5');
      setRecentLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const handleFaceDetected = async (data) => {
    setIsProcessing(true);
    
    try {
      const response = await axios.post('/api/faces/recognize', {
        face_vector: data.vector,
      });
      
      setResult(response.data);
      
      // Refresh stats and logs
      fetchStats();
      fetchLogs();
    } catch (error) {
      setResult({
        matched: false,
        error: error.response?.data?.error || 'Recognition failed',
      });
    }
    
    setIsProcessing(false);
  };

  const getConfidenceColor = (confidence) => {
    const value = parseFloat(confidence);
    if (value >= 80) return '#00e676';
    if (value >= 60) return '#ffc107';
    return '#ff5252';
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-SG', {
      hour: '2-digit',
      minute: '2-digit',
      day: 'numeric',
      month: 'short',
    });
  };

  return (
    <>
      {/* Stats Row */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.total_persons || 0}</div>
          <div style={styles.statLabel}>Registered Persons</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.total_faces || 0}</div>
          <div style={styles.statLabel}>Face Embeddings</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.entries_today || 0}</div>
          <div style={styles.statLabel}>Entries Today</div>
        </div>
      </div>
      
      <div style={styles.container}>
        {/* Left: Face Capture */}
        <div style={styles.card}>
          <h2 style={styles.title}>🔍 Face Recognition</h2>
          <FaceCapture onFaceDetected={handleFaceDetected} mode="recognize" />
        </div>
        
        {/* Right: Result */}
        <div>
          <div
            style={{
              ...styles.card,
              ...styles.resultCard,
              ...(result?.matched ? styles.matchedCard : {}),
              ...(result && !result.matched ? styles.unmatchedCard : {}),
              ...(!result ? styles.waitingCard : {}),
            }}
          >
            {isProcessing ? (
              <>
                <div style={{ ...styles.avatar, ...styles.avatarWaiting }}>
                  ⏳
                </div>
                <p>Searching database...</p>
              </>
            ) : result ? (
              result.matched ? (
                <>
                  <div style={{ ...styles.avatar, ...styles.avatarMatched }}>
                    ✓
                  </div>
                  <div style={styles.personName}>{result.person.name}</div>
                  <div style={styles.personId}>ID: {result.person.person_id}</div>
                  
                  {/* Matched Face Label */}
                  {result.matched_face && (
                    <div style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      background: 'rgba(0, 217, 255, 0.2)',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      color: '#00d9ff',
                      marginBottom: '0.5rem',
                    }}>
                      Matched: {result.matched_face}
                    </div>
                  )}
                  
                  {/* Confidence Bar */}
                  <div style={styles.confidenceBar}>
                    <div style={styles.confidenceLabel}>
                      <span>Match Confidence</span>
                      <span style={{ color: getConfidenceColor(result.confidence) }}>
                        {result.confidence}%
                      </span>
                    </div>
                    <div style={styles.confidenceTrack}>
                      <div
                        style={{
                          ...styles.confidenceFill,
                          width: `${result.confidence}%`,
                          background: getConfidenceColor(result.confidence),
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Person Details */}
                  <div style={styles.detailsGrid}>
                    {result.person.department && (
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>Department</span>
                        <span style={styles.detailValue}>{result.person.department}</span>
                      </div>
                    )}
                    {result.person.designation && (
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>Designation</span>
                        <span style={styles.detailValue}>{result.person.designation}</span>
                      </div>
                    )}
                    {result.person.email && (
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>Email</span>
                        <span style={styles.detailValue}>{result.person.email}</span>
                      </div>
                    )}
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Distance</span>
                      <span style={styles.detailValue}>{result.distance}</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ ...styles.avatar, ...styles.avatarUnmatched }}>
                    ✗
                  </div>
                  <div style={styles.personName}>Unknown Person</div>
                  <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.5rem' }}>
                    {result.message || 'Face not recognized'}
                  </p>
                  
                  {result.top_matches && result.top_matches.length > 0 && (
                    <div style={styles.topMatches}>
                      <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>
                        Closest matches:
                      </p>
                      {result.top_matches.map((match, idx) => (
                        <div key={idx} style={styles.matchRow}>
                          <div>
                            <span>{match.name}</span>
                            {match.matched_face && (
                              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginLeft: '0.5rem' }}>
                                ({match.matched_face})
                              </span>
                            )}
                          </div>
                          <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                            {match.confidence}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )
            ) : (
              <div style={styles.instructions}>
                <div style={{ ...styles.avatar, ...styles.avatarWaiting }}>
                  👤
                </div>
                <p style={{ marginBottom: '0.5rem' }}>Ready to Scan</p>
                <p style={{ fontSize: '0.85rem' }}>
                  Position face in camera or upload a photo
                </p>
              </div>
            )}
          </div>
          
          {/* Recent Access Logs */}
          {recentLogs.length > 0 && (
            <div style={{ ...styles.card, marginTop: '1rem' }}>
              <h3 style={{ ...styles.title, fontSize: '1.1rem', marginBottom: '1rem' }}>
                📋 Recent Access
              </h3>
              {recentLogs.map((log, idx) => (
                <div key={idx} style={styles.logItem}>
                  <span>{log.name || log.person_id}</span>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                    {formatTime(log.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default RecognizePage;
